import { useMutation } from '@apollo/client/react'
import Router from 'next/router'
import { useState } from 'react'
import { useSnackbars } from 'settle-ui/hooks/useSnackbars/useSnackbars'
import { logEvent } from 'lib/tracking/segment'
import { RouterPage } from 'lib/navigation/consts'
import { payerInvitationRoutes } from 'lib/navigation/helpers/getPayerSignUpPath/getPayerSignUpPath'
import { getSource, type GetSourceParams } from 'routes/sign-up/payers/helpers/getSource/getSource'
import { useUpdatePayerInvitation } from 'routes/sign-up/payers/hooks/useUpdatePayerInvitation/useUpdatePayerInvitation'
import { useAppliedPromoCodeContext } from 'routes/hooks/useAppliedPromoCodeContext/useAppliedPromoCodeContext'
import { pushGtmDataLayerVariables } from 'lib/helpers/pushGtmDataLayerVariables/pushGtmDataLayerVariables'
import { isApolloErrorWithCode } from 'lib/helpers/isApolloErrorWithCode/isApolloErrorWithCode'
import * as events from '../../ProductPage.events'
import { useProductFormContext } from '../useProductForm/useProductForm'
import { useShopifyOauthArguments } from '../useShopifyOauthArguments/useShopifyOauthArguments'
import { ProductFormSubmitHook_createPayerInvitationDocument } from './useProductFormSubmit.graphql'
import { type BusinessTypeEnum, SignupInterestsEnum } from 'gql/types'

export interface UseProductFormSubmitParams extends Partial<GetSourceParams> {
  invitationCode: string | null
  accountingFirmId: string | null
  businessType: BusinessTypeEnum | null
  referralCode: string | undefined
}

export const useProductFormSubmit = ({
  referralCode,
  invitationCode,
  accountingFirmId,
  ...sourceParams
}: UseProductFormSubmitParams) => {
  const form = useProductFormContext()
  const [loading, setLoading] = useState(false)
  const { showApolloOrTextError } = useSnackbars()
  const { promoCode } = useAppliedPromoCodeContext()
  const [createInvitation] = useMutation(ProductFormSubmitHook_createPayerInvitationDocument)
  const shopifyOauthArguments = useShopifyOauthArguments(sourceParams)
  const { updatePayerInvitation, loading: updateLoading } = useUpdatePayerInvitation({
    onSuccess: () =>
      Router.push({
        pathname: payerInvitationRoutes.businessDetails,
        query: Router.query,
      }),
  })

  const handleSubmit = form.handleSubmit(async ({ interestedIn: interestedInValue, businessType, email }) => {
    try {
      pushGtmDataLayerVariables({ email })

      const interestedIn = [
        ...(interestedInValue.billPay ? [SignupInterestsEnum.BILL_PAY] : []),
        ...(interestedInValue.purchasing ? [SignupInterestsEnum.PURCHASING] : []),
        ...(interestedInValue.workingCapital ? [SignupInterestsEnum.WORKING_CAPITAL] : []),
        ...(interestedInValue.landedCosts ? [SignupInterestsEnum.LANDED_COSTS] : []),
      ]

      if (interestedIn) {
        void logEvent(events.SUBMIT, { interestedIn })
        const billingPromotionCodeId = promoCode?.id && promoCode.applied ? promoCode.id : undefined
        const source = getSource({ ...sourceParams, referring_url: document.referrer })

        if (invitationCode) {
          await updatePayerInvitation({
            interestedIn,
            invitationCode,
            email: email,
            billingPromotionCodeId,
            businessType: businessType,
            source,
          })
        } else {
          setLoading(true)
          const { data } = await createInvitation({
            variables: {
              interestedIn,
              accountingFirmId,
              shopifyOauthArguments,
              email: email,
              billingPromotionCodeId,
              businessType: businessType,
              source,
              ...(referralCode && { referralCode }),
            },
          })

          await Router.push({
            pathname: payerInvitationRoutes.businessDetails,
            query: {
              ...Router.query,
              invitation_code: data?.createPayerInvitation?.invitationCode,
            },
          })
        }
      }
    } catch (err) {
      if (isApolloErrorWithCode(err, { code: 'payer_onboarding_email_exists' })) {
        void Router.push({ pathname: RouterPage.login, query: { email } })
      }
      showApolloOrTextError(err)
    } finally {
      setLoading(false)
    }
  })

  return {
    handleSubmit,
    loading: loading || updateLoading,
  }
}
